import { LinearProgress } from '@mui/material';
import { ApolloQueryResult, OperationVariables, useMutation, useQuery } from '@parkchirp/api-client';
import Script from 'next/script';
import { FC, useCallback, useEffect, useState } from 'react';

import { SAVE_CREDIT_CARD_V2, type SaveCreditCardResult } from '../../../mutations';
import { GET_SLIDEPAY_HOSTED_FIELDS_TOKEN } from '../../../queries';
import {
	HostedFieldsErrorEvent,
	HostedFieldsSubmitEvent,
	HostedFieldsTokenResolverResponse,
	PaymentProvidersById,
	SetShowSnackbar,
	SlidePayFormConfig,
} from '../../../types';
import SlidePayCreditCardFields from './slidePayCreditCardFields';
import { slidePayDefaultFormConfig } from './slidePayFormConfig';

declare const HostedFields: {
	create: (args: any) => EventTarget;
};

type FormProps = {
	cognitoSub?: string | null;
	facilityId?: string;
	subscriptionId?: string;
	tenantSubscriptionId?: string;
	cancelButton?: any;
	configOverride?: (token: string) => SlidePayFormConfig;
	onCardSaved?: (result: SaveCreditCardResult) => void;
	refetchPaymentMethods?: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	setShowSnackbar: SetShowSnackbar;
};

const generateFormId = () => (Math.random() * 100).toString();

export const SlidePayCreditCardForm: FC<FormProps> = ({
	cognitoSub,
	facilityId,
	subscriptionId,
	tenantSubscriptionId,
	cancelButton,
	configOverride,
	onCardSaved,
	refetchPaymentMethods,
	setShowSnackbar,
}) => {
	const [hostedFieldsToken, setHostedFieldsToken] = useState('');
	const [tokenIsValid, setTokenIsValid] = useState(false);
	const [scriptReady, setScriptReady] = useState(false);
	const [formConfig, setFormConfig] = useState<any>(null);
	const [formId, setFormId] = useState<string>(generateFormId());
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formRendered, setFormRendered] = useState(false);

	const hostedFieldsVariables: Record<string, string | undefined> = {
		origin: window.location.origin,
	};

	if (facilityId) {
		hostedFieldsVariables.facilityId = facilityId;
	}

	if (subscriptionId || tenantSubscriptionId) {
		hostedFieldsVariables.subscriptionId = tenantSubscriptionId ?? subscriptionId;
	}

	const config = configOverride ?? slidePayDefaultFormConfig;

	const { refetch: refetchHostedFieldsToken } = useQuery(GET_SLIDEPAY_HOSTED_FIELDS_TOKEN, {
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
		context: { apiVersion: 'v2', public: true },
		variables: hostedFieldsVariables,
		onCompleted: (data: HostedFieldsTokenResolverResponse) => {
			const token = data.getSlidePayHostedFieldsToken.hostedFieldsToken;
			setHostedFieldsToken(token);
			setFormConfig(config(token));
			setTokenIsValid(true);
		},
		onError: () => {
			setShowSnackbar({
				message: 'Error initializing credit card form',
				severity: 'error',
				autoHideDuration: 5000,
			});

			setTokenIsValid(false);
		},
	});

	const [saveCreditCard] = useMutation(SAVE_CREDIT_CARD_V2, {
		context: { apiVersion: 'v2', public: true },
		onCompleted: (response: { saveCreditCard: SaveCreditCardResult }) => {
			setShowSnackbar({
				message: 'Credit card saved successfully',
				severity: 'success',
				autoHideDuration: 3000,
			});

			if (refetchPaymentMethods) refetchPaymentMethods();
			refetchHostedFieldsToken();
			setIsSubmitting(false);

			if (onCardSaved) {
				onCardSaved(response.saveCreditCard);
			}
		},
		onError: (error) => {
			setShowSnackbar({
				message: error.message,
				severity: 'error',
				autoHideDuration: 5000,
			});

			refetchHostedFieldsToken();
			setIsSubmitting(false);
		},
	});

	const onFormRendered = useCallback(() => {
		setFormRendered(true);
	}, [setFormRendered]);

	const onFormSubmit = useCallback(() => {
		setIsSubmitting(true);
	}, [setIsSubmitting]);

	const onFormSubmitResponse = useCallback(
		async (event: HostedFieldsSubmitEvent) => {
			if (!event.detail.result) {
				setShowSnackbar({
					message: 'Failed to initialize credit card form',
					severity: 'error',
				});

				setIsSubmitting(false);
				return;
			}

			await saveCreditCard({
				variables: {
					cognitoSub,
					token: hostedFieldsToken,
					paymentProviderId: PaymentProvidersById.SlidePay,
				},
			});

			setTokenIsValid(false);
			setFormId(generateFormId());
			setIsSubmitting(false);
		},
		[cognitoSub, hostedFieldsToken, saveCreditCard, setTokenIsValid, setShowSnackbar],
	);

	const onFormError = useCallback(
		(event: HostedFieldsErrorEvent) => {
			if (!tokenIsValid) {
				return;
			}

			setShowSnackbar({
				message: `Failed to submit credit card form: ${event.detail.message}`,
				severity: 'error',
				autoHideDuration: 10000,
			});
		},
		[tokenIsValid, setShowSnackbar],
	);

	useEffect(() => {
		if (!scriptReady || !formConfig || !formId || !tokenIsValid) {
			return undefined;
		}

		const form = HostedFields.create(formConfig);

		form.addEventListener('hostedFields.ready', onFormRendered);
		form.addEventListener('submit.processing', onFormSubmit);
		form.addEventListener('submit.result', onFormSubmitResponse as unknown as EventListener);
		form.addEventListener('hostedFields.error', onFormError as unknown as EventListener);

		return () => {
			form.removeEventListener('hostedFields.ready', onFormRendered);
			form.removeEventListener('submit.processing', onFormSubmit);
			form.removeEventListener('submit.result', onFormSubmitResponse as unknown as EventListener);
			form.removeEventListener('hostedFields.error', onFormError as unknown as EventListener);
		};
	}, [
		formId,
		formConfig,
		hostedFieldsToken,
		scriptReady,
		tokenIsValid,
		onFormRendered,
		onFormSubmit,
		onFormError,
		onFormSubmitResponse,
	]);

	return (
		<div className='pca__slidePay-hosted-fields'>
			{!formRendered && <LinearProgress />}
			<SlidePayCreditCardFields key={formId} isSubmitting={isSubmitting} CancelButton={cancelButton} />
			<Script
				id='slidepay-hosted-fields-script'
				src='https://slidepaydashboard.com/js/HostedFields.js'
				strategy='afterInteractive'
				onReady={() => {
					setScriptReady(true);
					setFormRendered(false);
				}}
				onError={() => {
					setShowSnackbar({
						message: 'Failed to load SlidePay script',
						severity: 'error',
						autoHideDuration: 5000,
					});
				}}
			/>
		</div>
	);
};
